/**
 * The term comparison scatter, drawn with Observable Plot.
 *
 * A click picks the term under the pointer — the same one the tip is naming —
 * and the page takes that to the schedule. `PlotFigure` handles the drawing.
 */
import m from 'mithril';
import * as Plot from '@observablehq/plot';

import type { Cost } from './types.ts';
import { asCurrency } from './format.ts';
import { PlotFigure } from './plot.ts';

interface GraphAttrs {
    costs: Cost[];
    onpick?: (term: number) => void;
}

export const Graph: m.Component<GraphAttrs> = {
    view: ({ attrs: { costs, onpick } }) =>
        m(PlotFigure, {
            class: 'graph',
            data: costs,
            draw: (width, height) => costChart(costs, width, height),
            onpick: onpick && ((cost: Cost) => onpick(cost.term)),
        }),
};

function costChart(costs: Cost[], width: number, height: number) {
    // On a phone the axis gutters are a third of the chart, so they shrink and
    // the ticks thin out.
    const compact = width < 480;

    // Shared by the scatter and by the pointer's restatement of one point.
    const circle = {
        x: 'monthly_cost',
        y: 'loan_cost',
        r: 9,
        stroke: 'var(--accent-deep)',
        strokeWidth: 1,
    };
    const label = {
        x: 'monthly_cost',
        y: 'loan_cost',
        text: (d) => String(d.term),
        fill: '#ffffff',
        fontSize: 10,
        fontWeight: 600,
        pointerEvents: 'none',
    };

    return Plot.plot({
        width,
        height,
        marginTop: 20,
        marginRight: compact ? 14 : 20,
        marginBottom: compact ? 42 : 50,
        marginLeft: compact ? 46 : 56,
        style: {
            background: 'transparent',
            color: 'var(--ink-soft)',
            fontFamily: 'inherit',
            fontSize: '11px',
        },
        x: {
            domain: [
                0,
                snapTo(
                    2000,
                    largest(costs, (d) => d.monthly_cost),
                ),
            ],
            label: 'Monthly repayment',
            labelAnchor: 'center',
            labelArrow: 'none',
            // Plot would sit it right on the panel's bottom edge.
            labelOffset: compact ? 32 : 38,
            ticks: compact ? 4 : 8,
            tickFormat: thousands,
        },
        y: {
            domain: [
                0,
                snapTo(
                    20000,
                    largest(costs, (d) => d.loan_cost),
                ),
            ],
            label: 'Total cost',
            labelAnchor: 'center',
            labelArrow: 'none',
            ticks: compact ? 5 : 10,
            tickFormat: thousands,
        },
        marks: [
            // Plot draws ticks but no axis line, and the design has always had
            // one down the left and along the bottom. A frame edge rather than
            // a rule at 0: with the fields cleared there is no data, both
            // domains collapse to [0, 0], and a rule would be stranded in the
            // middle of an empty chart.
            Plot.frame({ anchor: 'bottom', stroke: 'var(--line-strong)' }),
            Plot.frame({ anchor: 'left', stroke: 'var(--line-strong)' }),

            // A mark per point, circle then label, rather than every circle
            // followed by every label. Paint order is DOM order in SVG, so
            // this is what lets a crowded label disappear behind the next
            // circle along instead of overprinting it — where the terms bunch
            // up, two numbers on top of each other read as neither.
            ...costs.flatMap((cost) => [
                Plot.dot([cost], { ...circle, fill: 'var(--accent-bright)' }),
                Plot.text([cost], label),
            ]),

            // The point under the pointer, restated in the hover colour, with
            // its own number back on top of it.
            Plot.dot(costs, Plot.pointer({ ...circle, fill: 'var(--accent)' })),
            Plot.text(costs, Plot.pointer(label)),

            // Nearest-point hover rather than hit the 9px circle, so a crowded
            // stretch of the curve is still readable, and a tap works on a
            // phone. Plot flips the box to keep it inside the frame.
            Plot.tip(
                costs,
                Plot.pointer({
                    x: 'monthly_cost',
                    y: 'loan_cost',
                    title: (d) =>
                        `${d.term} years\n` +
                        `${asCurrency(d.monthly_cost)} a month · ` +
                        `${asCurrency(d.loan_cost)} interest`,
                    fill: 'var(--ink)',
                    stroke: 'var(--ink)',
                    lineHeight: 1.3,
                }),
            ),
        ],
    });
}

/** 0 for an empty cost list, which used to make the whole domain NaN. */
function largest(costs: Cost[], of: (cost: Cost) => number) {
    return costs.reduce((best, cost) => Math.max(best, of(cost)), 0);
}

/** 140000 as 140k. Both axes only ever tick in whole thousands. */
function thousands(value) {
    return value === 0 ? '0' : `${value / 1000}k`;
}

function snapTo(interval, number) {
    return Math.ceil(number / interval) * interval;
}
